import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IOption } from '@cmps/ng-select';
import { Store } from 'redux';
import { Subscription } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { CmpsDatePipe } from '../common/date.pipe';
import {
    CmpsForm,
    CmpsFormBuilder,
    CmpsFormField,
    CmpsFormFields,
} from '../common/experimental.form';
import { StateToProps, storeObserver } from '../store';
import { STORE } from '../store.metadata';

import {
    reportFormValueChange,
    reportGenerate,
    reportRetrieveInitData,
} from './tdcf-form.action';
import { TranslationKeyMap } from './tdcf-form.i18n';
import { ManagerReportState, storeStateMapper } from './tdcf-form.state';

interface FormFieldsKey extends CmpsFormFields {
    DATE_RANGES: CmpsFormField;
}

const FormFields: FormFieldsKey = {
    DATE_RANGES: {
        id: `dateRanges${Math.random()}`,
        name: 'dateRanges',
        labelId: `dateRanges${Math.random()}`,
        label: TranslationKeyMap._201_QUARTER,
        placeholder: TranslationKeyMap._7209_SELECT_QUARTERS,
        validators: [Validators.required],
        validationMsgs: {
            required:
                TranslationKeyMap._645__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_SELECT_A__FIELD_LABEL_,
        },
    },
};

@Component({
    selector: '[cmpsTdcfReportManager]',
    templateUrl: './manager-report.tdcf.component.html',
    host: {
        class: 'cmps-tdcf-report-manager',
    },
})
export class ManagerReportTdcfComponent
    implements OnInit, OnDestroy, StateToProps<ManagerReportState>
{
    i18n = TranslationKeyMap;
    form: CmpsForm<FormFieldsKey>;
    private _storeSub: Subscription;
    state: ManagerReportState;
    private _routeSub: Subscription;
    templateId: number;
    private _formSub: Subscription;
    reviewDateRangeOpts: IOption[];
    directTeamOnly: boolean;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _activatedRoute: ActivatedRoute,
        @Inject(STORE) private readonly _store: Store,
        private readonly _formBuilder: CmpsFormBuilder,
        private readonly _datePipe: CmpsDatePipe,
        private readonly _authService: AuthService,
    ) {
        this.form = this._formBuilder.create(FormFields);
    }

    ngOnInit(): void {
        this._storeSub = storeObserver<ManagerReportState>(
            this._store,
            (state) => storeStateMapper(state).managerReport,
        )
            .pipe(
                tap((state) =>
                    this._ngZone.run(() => {
                        this.state = state;
                        this.stateToProps();
                    }),
                ),
            )
            .subscribe();

        this.directTeamOnly = this._activatedRoute.snapshot.data.directTeamOnly;

        this._routeSub = this._activatedRoute.queryParams
            .pipe(
                // eslint-disable-next-line no-prototype-builtins
                filter((queryParams) => queryParams.hasOwnProperty('tpl')),
                tap((queryParams) => {
                    this.templateId = queryParams.tpl;
                    this._store.dispatch(
                        reportRetrieveInitData(this.templateId),
                    );
                }),
            )
            .subscribe();

        this._formSub = this.form.form.valueChanges
            .pipe(
                debounceTime(500),
                tap((value) =>
                    this._store.dispatch(reportFormValueChange(value)),
                ),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        if (this._formSub) {
            this._formSub.unsubscribe();
        }
        if (this._routeSub) {
            this._routeSub.unsubscribe();
        }
        if (this._storeSub) {
            this._storeSub.unsubscribe();
        }
    }

    stateToProps(): void {
        if (this.state.metadata) {
            this.reviewDateRangeOpts = this.state.metadata.reviewDateRanges.map(
                (dr) => ({
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    label: `${dr.displayName} (${this._datePipe.transform(
                        dr.start,
                        'MMMM dd, y',
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    )} - ${this._datePipe.transform(dr.end, 'MMMM dd, y')})`,
                    value: dr,
                }),
            );
        }

        if (this.state.formValue) {
            this.form.form.patchValue(this.state.formValue, {
                emitEvent: false,
            });
        }
    }

    onSubmit(): void {
        if (!this.form.form.valid) {
            this.form.checkWarningsErrors();
            return;
        }
        const userId = this._authService.user.id;
        this._store.dispatch(
            reportGenerate(
                this.templateId,
                userId,
                this.form.form.controls[this.form.fields.DATE_RANGES.name]
                    .value,
                this.directTeamOnly,
            ),
        );
    }
}
